import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { apiClient } from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { toastHelpers } from '@/lib/toast-helpers'
import { useCurrency } from '@/contexts/CurrencyContext'
import { currencyInputPrefix } from '@/lib/formatMoney'
import { cn } from '@/lib/utils'
import {
  choicesSummary,
  draftFromGroup,
  draftProblem,
  emptyDraft,
  groupPayload,
  isRequired,
  kindOf,
  optionsPayload,
  ruleOf,
  rulePill,
  usageCounts,
  type VariantDraft,
} from '@/lib/variantShape'
import { TEMPLATE_PACKS, type TemplatePack } from '@/lib/variantTemplates'
import { draftFromTemplate } from '@/lib/productVariants'
import type { ModifierGroup } from '@/types'
import { OptionIngredients } from '@/components/admin/costing/OptionIngredients'
import { VariantListEditor } from './VariantListEditor'
import { CashierPreview } from './CashierPreview'

/**
 * Shared lists (F-MOD-N12): the old Modifiers library, demoted.
 *
 * Only for editing a list that several items share, or adding a cuisine
 * pack. Each row says where it is used; nobody has to come here to get an
 * item selling. Editing opens the same inline editor the product form uses,
 * with the cashier's phone beside it.
 */
export function SharedListsTab() {
  const qc = useQueryClient()
  const { formatCurrency, currencyCode } = useCurrency()
  const prefix = currencyInputPrefix(currencyCode)

  const { data: groups = [], isLoading } = useQuery({
    queryKey: ['modifier-groups'],
    queryFn: async () => { const r = await apiClient.getModifierGroups(); if (!r.success) throw new Error(r.message); return r.data ?? [] },
  })
  const { data: usageCfg } = useQuery({
    queryKey: ['variant-usage'],
    queryFn: async () => { const r = await apiClient.getMenuModifiers(); if (!r.success) throw new Error(r.message); return r.data },
  })
  const usage = useMemo(() => usageCounts(usageCfg), [usageCfg])

  const [editing, setEditing] = useState<VariantDraft | null>(null)
  const [packOpen, setPackOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<ModifierGroup | null>(null)

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['modifier-groups'] })
    qc.invalidateQueries({ queryKey: ['modifier-attachments'] })
    qc.invalidateQueries({ queryKey: ['variant-usage'] })
    qc.invalidateQueries({ queryKey: ['menu-modifiers'] })
    qc.invalidateQueries({ queryKey: ['product-modifier-groups'] })
  }

  const save = useMutation({
    mutationFn: async (d: VariantDraft) => {
      if (d.id) {
        const g = await apiClient.updateModifierGroup(d.id, groupPayload(d))
        if (!g.success) throw new Error(g.message)
        const o = await apiClient.replaceModifierOptions(d.id, optionsPayload(d))
        if (!o.success) throw new Error(o.message)
        return d.id
      }
      const nextSort = (groups.length > 0 ? groups[groups.length - 1].sort_order : 0) + 10
      const g = await apiClient.createModifierGroup(groupPayload({ ...d, sortOrder: nextSort }))
      if (!g.success || !g.data?.id) throw new Error(g.message)
      const o = await apiClient.replaceModifierOptions(g.data.id, optionsPayload(d))
      if (!o.success) throw new Error(o.message)
      return g.data.id
    },
    onSuccess: (_id, d) => {
      invalidate()
      setEditing(null)
      toastHelpers.success(d.id ? 'List saved' : 'List created', d.name)
    },
    onError: (e: Error) => toastHelpers.error('Could not save the list', e.message),
  })

  const remove = useMutation({
    mutationFn: async (id: string) => { const r = await apiClient.deleteModifierGroup(id); if (!r.success) throw new Error(r.message) },
    onSuccess: () => { invalidate(); setConfirmDelete(null); setEditing(null); toastHelpers.success('List deleted', 'Past orders keep the names they were sold under.') },
    onError: (e: Error) => toastHelpers.error('Could not delete the list', e.message),
  })

  const installPack = useMutation({
    mutationFn: async (pack: TemplatePack) => {
      // Create every list, then wire the cross-list rules once ids exist.
      const existing = new Set(groups.map((g) => g.name.toLowerCase()))
      const created = new Map<string, { groupId: string; optionIds: string[] }>()
      let sort = (groups.length > 0 ? groups[groups.length - 1].sort_order : 0) + 10
      let skipped = 0
      for (const t of pack.templates) {
        if (existing.has(t.name.toLowerCase())) { skipped += 1; continue }
        const d = draftFromTemplate(t)
        const g = await apiClient.createModifierGroup(groupPayload({ ...d, sortOrder: sort }))
        if (!g.success || !g.data?.id) throw new Error(g.message || `Could not create ${t.name}`)
        const o = await apiClient.replaceModifierOptions(g.data.id, optionsPayload(d))
        if (!o.success) throw new Error(o.message || `Could not add choices to ${t.name}`)
        created.set(t.key, { groupId: g.data.id, optionIds: o.data?.option_ids ?? [] })
        sort += 10
      }
      for (const t of pack.templates) {
        const me = created.get(t.key)
        if (!me || !t.choices.some((c) => c.links)) continue
        const d = draftFromTemplate(t)
        const rows = optionsPayload(d).map((row, i) => {
          const link = t.choices[i]?.links
          const target = link ? created.get(link.target) : undefined
          return {
            ...row,
            id: me.optionIds[i],
            group_rules: link && target ? [{ target_group_id: target.groupId, min_select: link.min, max_select: link.max }] : [],
          }
        })
        const o = await apiClient.replaceModifierOptions(me.groupId, rows)
        if (!o.success) throw new Error(o.message || `Could not wire ${t.name}`)
      }
      return { created: created.size, skipped }
    },
    onSuccess: (r, pack) => {
      invalidate()
      setPackOpen(false)
      toastHelpers.success(`${pack.name} added`, `${r.created} list${r.created === 1 ? '' : 's'} created${r.skipped ? `, ${r.skipped} already existed` : ''}. Attach them on the matrix or on an item.`)
    },
    onError: (e: Error) => toastHelpers.error('Could not add the pack', e.message),
  })

  const problem = editing ? draftProblem(editing) : null
  const otherGroups = (id?: string) => groups.filter((g) => g.id !== id)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-xl text-sm text-muted-foreground">
          Lists several items ask. Edit one here and every item that uses it changes. To set up a single item, open the item instead.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setPackOpen(true)}>Add a template pack</Button>
          <Button size="sm" className="gap-1.5" onClick={() => setEditing(emptyDraft('one'))}>
            <Plus className="h-3.5 w-3.5" /> New list
          </Button>
        </div>
      </div>

      {editing && !editing.id && (
        <EditorCard
          draft={editing}
          onChange={setEditing}
          onCancel={() => setEditing(null)}
          onSave={() => save.mutate(editing)}
          saving={save.isPending}
          problem={problem}
          otherGroups={otherGroups()}
          prefix={prefix}
          formatCurrency={formatCurrency}
        />
      )}

      {isLoading ? (
        <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
      ) : groups.length === 0 ? (
        <p className="rounded-lg border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
          No shared lists yet. Open an item and type a question, or add a template pack.
        </p>
      ) : (
        <div className="overflow-hidden rounded-lg border">
          <div className="grid grid-cols-[1.2fr_2fr_1fr_auto] gap-3 border-b bg-muted/60 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground max-sm:grid-cols-[1fr_auto]">
            <span>List</span><span className="max-sm:hidden">Choices</span><span className="max-sm:hidden">Used on</span><span />
          </div>
          {groups.map((g) => {
            const count = usage.get(g.id) ?? 0
            const open = editing?.id === g.id
            const kind = kindOf(g)
            const rule = ruleOf(g)
            return (
              <div key={g.id} className={cn('border-b last:border-b-0', open && 'bg-muted/20')}>
                <div className="grid grid-cols-[1.2fr_2fr_1fr_auto] items-center gap-3 px-3 py-2 max-sm:grid-cols-[1fr_auto]">
                  <div className="min-w-0">
                    <p className={cn('truncate text-sm font-medium', !g.is_active && 'text-muted-foreground')}>{g.name}{!g.is_active && ' · off'}</p>
                    <p className="truncate text-xs text-muted-foreground">{g.prompt}</p>
                  </div>
                  <div className="min-w-0 truncate text-xs text-muted-foreground max-sm:hidden">
                    {choicesSummary(g, formatCurrency)}{' '}
                    <Badge variant={isRequired(rule) ? 'secondary' : 'outline'} className="ml-1 text-[10px]">{kind === 'size' ? 'sets price' : rulePill(kind, rule)}</Badge>
                  </div>
                  <p className={cn('text-xs max-sm:hidden', count === 0 && 'text-amber-700 dark:text-amber-400')}>
                    {count === 0 ? 'Not used yet' : `${count} item${count === 1 ? '' : 's'}`}
                  </p>
                  <div className="flex gap-1">
                    {!open && (
                      <Button size="sm" variant="ghost" className="h-7 gap-1 px-2" onClick={() => setEditing(draftFromGroup(g))}>
                        <Pencil className="h-3 w-3" /> Edit
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive" aria-label={`Delete ${g.name}`} onClick={() => setConfirmDelete(g)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                {open && editing && (
                  <div className="border-t p-3">
                    <EditorCard
                      draft={editing}
                      onChange={setEditing}
                      onCancel={() => setEditing(null)}
                      onSave={() => save.mutate(editing)}
                      saving={save.isPending}
                      problem={problem}
                      otherGroups={otherGroups(g.id)}
                      prefix={prefix}
                      formatCurrency={formatCurrency}
                      sharedCount={count}
                      renderIngredients={(c) => <OptionIngredients optionId={c.id} optionName={c.name} />}
                      bare
                    />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {confirmDelete && (
        <Dialog open onOpenChange={(o) => !o && setConfirmDelete(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Delete “{confirmDelete.name}”?</DialogTitle>
              <DialogDescription>
                {usage.get(confirmDelete.id) ? `It is asked on ${usage.get(confirmDelete.id)} item${usage.get(confirmDelete.id) === 1 ? '' : 's'}; they stop asking it. ` : ''}
                Past orders keep the names they were sold under.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDelete(null)}>Keep it</Button>
              <Button variant="destructive" disabled={remove.isPending} onClick={() => remove.mutate(confirmDelete.id)}>Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={packOpen} onOpenChange={setPackOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add a template pack</DialogTitle>
            <DialogDescription>Ready-made lists for a kind of kitchen. They are created unattached; tick them on in the matrix or on an item. Lists you already have by the same name are skipped.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 sm:grid-cols-2">
            {TEMPLATE_PACKS.map((p) => (
              <button
                key={p.key}
                type="button"
                disabled={installPack.isPending}
                onClick={() => installPack.mutate(p)}
                className="rounded-lg border p-3 text-left hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
              >
                <p className="text-sm font-semibold">{p.name}</p>
                <p className="text-xs text-muted-foreground">{p.blurb}</p>
                <ul className="mt-1.5 text-xs text-muted-foreground">
                  {p.templates.map((t) => (
                    <li key={t.key}><span className="text-foreground">{t.name}</span> · {t.choices.map((c) => c.name).slice(0, 4).join(', ')}{t.choices.length > 4 ? '…' : ''}</li>
                  ))}
                </ul>
              </button>
            ))}
          </div>
          {installPack.isPending && <p className="text-xs text-muted-foreground">Creating lists…</p>}
        </DialogContent>
      </Dialog>
    </div>
  )
}

function EditorCard({
  draft, onChange, onCancel, onSave, saving, problem, otherGroups, prefix, formatCurrency, sharedCount, renderIngredients, bare,
}: {
  draft: VariantDraft
  onChange: (d: VariantDraft) => void
  onCancel: () => void
  onSave: () => void
  saving: boolean
  problem: string | null
  otherGroups: ModifierGroup[]
  prefix: string
  formatCurrency: (n: number) => string
  sharedCount?: number
  renderIngredients?: (c: { id: string; name: string }) => React.ReactNode
  bare?: boolean
}) {
  return (
    <div className={cn('grid gap-5 lg:grid-cols-[minmax(0,1fr)_15rem]', !bare && 'rounded-lg border border-primary p-4')}>
      <div className="space-y-4">
        {!bare && <p className="text-sm font-semibold">New list</p>}
        <VariantListEditor
          draft={draft}
          onChange={onChange}
          otherGroups={otherGroups}
          currencyPrefix={prefix}
          sharedCount={sharedCount}
          renderIngredients={renderIngredients}
        />
        <div className="flex items-center justify-between gap-2 border-t pt-3">
          <span className="text-xs text-muted-foreground">{problem ?? (sharedCount && sharedCount > 1 ? `Saving changes ${sharedCount} items.` : '')}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onCancel} disabled={saving}>Cancel</Button>
            <Button size="sm" onClick={onSave} disabled={saving || problem != null}>{saving ? 'Saving…' : draft.id ? 'Save list' : 'Create list'}</Button>
          </div>
        </div>
      </div>
      <aside className="space-y-2">
        <p className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Cashier’s view <span className="font-mono text-[10px] text-primary">● live</span>
        </p>
        <CashierPreview
          productName="Example item"
          basePrice={draft.kind === 'size' ? 0 : 500}
          questions={[{ key: draft.id ?? 'new', prompt: draft.prompt, kind: draft.kind, rule: draft.rule, choices: draft.choices }]}
          formatCurrency={formatCurrency}
          footnote={`Shown on an example item at ${formatCurrency(500)}.`}
        />
      </aside>
    </div>
  )
}
